// IndexedDB-backed cache for turbine photo lists.
//
// Goal: screens should not hit the backend (DB query + presigned URL signing
// for every photo) on every visit. The full photo list of a turbine is stored
// here and kept up to date with a delta-style sync: a lightweight
// `/photos/sync-state` endpoint (COUNT + MAX(uploaded_at)) is compared against
// the stored state, and the full list is only re-fetched when they differ or
// when the stored presigned URLs are close to expiry.

import type { Photo } from '../services/turbineService'

export interface PhotoSyncState {
  count: number
  maxUploadedAt: string | null
}

export interface StoredPhotoList {
  turbineId: number
  photos: Photo[]
  syncState: PhotoSyncState | null
  /** epoch ms of when the presigned URLs in `photos` were generated */
  urlsFetchedAt: number
}

const DB_NAME = 'deepwind-photo-cache'
const DB_VERSION = 1
const STORE_NAME = 'photoLists'

// Presigned URLs are valid for 6h; refresh well before they expire.
export const URL_MAX_AGE_MS = 5 * 60 * 60 * 1000 // 5 hours

let dbPromise: Promise<IDBDatabase | null> | null = null

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null)
      return
    }

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'turbineId' })
        }
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        console.warn('IndexedDB photo cache unavailable:', request.error)
        resolve(null)
      }
      request.onblocked = () => resolve(null)
    } catch (error) {
      console.warn('IndexedDB photo cache unavailable:', error)
      resolve(null)
    }
  })

  return dbPromise
}

export const photoDb = {
  async get(turbineId: number): Promise<StoredPhotoList | null> {
    const db = await openDb()
    if (!db) return null

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readonly')
        const request = tx.objectStore(STORE_NAME).get(turbineId)
        request.onsuccess = () => resolve((request.result as StoredPhotoList) || null)
        request.onerror = () => resolve(null)
      } catch {
        resolve(null)
      }
    })
  },

  async set(entry: StoredPhotoList): Promise<void> {
    const db = await openDb()
    if (!db) return

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        tx.objectStore(STORE_NAME).put(entry)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  },

  async delete(turbineId: number): Promise<void> {
    const db = await openDb()
    if (!db) return

    return new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        tx.objectStore(STORE_NAME).delete(turbineId)
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
  }
}

export const isUrlsFresh = (entry: StoredPhotoList): boolean =>
  Date.now() - entry.urlsFetchedAt < URL_MAX_AGE_MS

export const syncStatesEqual = (a: PhotoSyncState | null, b: PhotoSyncState | null): boolean => {
  if (!a || !b) return false
  const normalize = (value: string | null) => (value ? new Date(value).getTime() : null)
  return a.count === b.count && normalize(a.maxUploadedAt) === normalize(b.maxUploadedAt)
}
